import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import { useColumnVisibility } from "../../../hooks/useColumnVisibility";
import AppPage from "../../../components/CustomUI/AppPage";
import ListViewOptionsPanel from "../shared/ListViewOptionsPanel";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Select from "../../../components/BaseUI/Select";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import { FiPlus, FiEdit2, FiTrash2, FiChevronDown } from "react-icons/fi";
import { withAuth } from "../../../components/HOC/withAuth";
import "./PartVariants.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const variantDisplay = (v) => v?.variant ?? v?.prefix ?? "";

const tableColumns = [
  {
    name: "Varyant",
    dataType: "text",
    id: 1,
    customSorter: (a, b) =>
      String(variantDisplay(a)).localeCompare(String(variantDisplay(b))),
  },
  {
    name: "Notlar",
    dataType: "text",
    id: 2,
    customSorter: (a, b) =>
      String(a?.notes || a?.label || "").localeCompare(
        String(b?.notes || b?.label || ""),
      ),
  },
  {
    name: "Durum",
    dataType: "text",
    id: 3,
    customSorter: (a, b) => Number(!!b?.isActive) - Number(!!a?.isActive),
  },
  { name: "İşlemler", id: 4, sortable: false },
];

const emptyForm = () => ({ variant: "", notes: "", isActive: true });

const PartVariants = () => {
  useTitle("Rovion MES - Parça Varyantları");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const canWrite =
    !!currentUser &&
    (hasPageWriteAccess(
      currentUser.role,
      "/part-variants",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    ) ||
      hasPageWriteAccess(
        currentUser.role,
        "/parts",
        currentUser.pages || [],
        !!currentUser.fullAccess,
      ));

  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [sidebarOpened, setSidebarOpened] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    id: null,
    label: "",
    relatedParts: [],
    loadingParts: false,
  });
  const [deleting, setDeleting] = useState(false);
  const [relatedPartsOpen, setRelatedPartsOpen] = useState(false);

  const { visibleColumns, toggleColumnVisibility, resetToDefault } =
    useColumnVisibility("part-variants-list", tableColumns);

  const dismissToast = useCallback(
    (id) => setToasts((prev) => prev.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((prev) => [...prev, { id: Date.now(), message, type }]);
  }, []);
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${getToken()}` } }),
    [getToken],
  );

  const fetchVariants = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API_URL}/api/part-variants`,
        authHeaders(),
      );
      setVariants(data.variants || []);
    } catch {
      addToast("Varyantlar yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, addToast]);

  useEffect(() => {
    fetchVariants();
  }, [fetchVariants]);

  const openCreate = () => {
    setFormData(emptyForm());
    setEditingItem(null);
    setSidebarOpened(true);
  };

  const openEdit = (item) => {
    setFormData({
      variant: variantDisplay(item),
      notes: item.notes || item.label || "",
      isActive: item.isActive !== false,
    });
    setEditingItem(item);
    setSidebarOpened(true);
  };

  const closeSidebar = () => {
    setSidebarOpened(false);
    setEditingItem(null);
    setFormData(emptyForm());
  };

  const handleSave = async () => {
    if (saving) return;
    if (!formData.variant.trim()) {
      addToast("Varyant zorunludur", "danger");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        variant: formData.variant.trim(),
        notes: formData.notes.trim() || undefined,
        isActive: formData.isActive,
      };
      if (editingItem) {
        await axios.put(
          `${API_URL}/api/part-variants/${editingItem._id}`,
          payload,
          authHeaders(),
        );
        addToast("Güncellendi", "success");
      } else {
        await axios.post(
          `${API_URL}/api/part-variants`,
          payload,
          authHeaders(),
        );
        addToast("Oluşturuldu", "success");
      }
      closeSidebar();
      fetchVariants();
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  const closeConfirmModal = () => {
    setRelatedPartsOpen(false);
    setConfirmModal({
      isOpen: false,
      id: null,
      label: "",
      relatedParts: [],
      loadingParts: false,
    });
  };

  const openDeleteConfirm = async (item) => {
    const id = item._id;
    const label = variantDisplay(item);
    setRelatedPartsOpen(false);
    setConfirmModal({
      isOpen: true,
      id,
      label,
      relatedParts: [],
      loadingParts: true,
    });
    try {
      const { data } = await axios.get(
        `${API_URL}/api/part-variants/${id}/related-parts`,
        authHeaders(),
      );
      setConfirmModal((s) => ({
        ...s,
        relatedParts: data.parts || [],
        loadingParts: false,
      }));
    } catch {
      setConfirmModal((s) => ({ ...s, loadingParts: false }));
      addToast("İlişkili parçalar yüklenemedi", "danger");
    }
  };

  const handleDelete = async () => {
    const { id, relatedParts } = confirmModal;
    if (!id) return;

    setDeleting(true);
    try {
      const params =
        relatedParts.length > 0 ? { deleteParts: "true" } : undefined;
      const { data } = await axios.delete(
        `${API_URL}/api/part-variants/${id}`,
        { ...authHeaders(), params },
      );
      if (data.partsInactivated > 0) {
        addToast(
          `Varyant silindi; ${data.partsInactivated} parça stok nedeniyle pasifleştirildi`,
          "success",
        );
      } else {
        addToast("Silindi", "success");
      }
      fetchVariants();
      closeConfirmModal();
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AppPage
      title="Parça Varyantları"
      actions={
        canWrite && (
          <Button
            appearance="primary-animated"
            onClick={openCreate}
            icon={<FiPlus />}
          >
            Yeni Varyant
          </Button>
        )
      }
    >
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "error" ? "danger" : t.type || "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      <Modal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        width="small"
      >
        <ModalHeader>Varyantı Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page mb-2">
            <strong>{confirmModal.label || "Varyant"}</strong> silinsin mi?
          </p>
          {confirmModal.loadingParts && (
            <p className="text-muted small mb-0">
              İlişkili parçalar kontrol ediliyor…
            </p>
          )}
          {!confirmModal.loadingParts &&
            confirmModal.relatedParts.length > 0 && (
              <div className="part-variant-delete-related">
                <p className="text-muted mb-2">
                  Onayladığınızda varyant ile birlikte bağlı{" "}
                  <strong>{confirmModal.relatedParts.length}</strong> parça da
                  silinir veya kaldırılır.
                </p>
                <div className="part-variant-delete-related__collapse">
                  <button
                    type="button"
                    className={`part-variant-delete-related__toggle${relatedPartsOpen ? " is-open" : ""}`}
                    onClick={() => setRelatedPartsOpen((o) => !o)}
                    aria-expanded={relatedPartsOpen}
                  >
                    <FiChevronDown size={16} aria-hidden />
                    <span>
                      İlişkili parçalar ({confirmModal.relatedParts.length})
                    </span>
                  </button>
                  {relatedPartsOpen && (
                    <div className="part-variant-delete-related__grid">
                      {confirmModal.relatedParts.map((p) => {
                        const mainLabel =
                          p.mainPartId?.code || p.mainPartId?.name || "";
                        const tip = [
                          p.code,
                          p.name,
                          mainLabel && `Ana: ${mainLabel}`,
                        ]
                          .filter(Boolean)
                          .join(" · ");
                        return (
                          <div
                            key={p._id}
                            className="part-variant-delete-related__tile"
                            title={tip}
                          >
                            <span className="part-variant-delete-related__tile-code">
                              {p.code || "—"}
                            </span>
                            {p.name && (
                              <span className="part-variant-delete-related__tile-name">
                                {p.name}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="danger"
              onClick={handleDelete}
              disabled={deleting || confirmModal.loadingParts}
            >
              {deleting ? "Siliniyor…" : "Sil"}
            </Button>
            <Button appearance="subtle" onClick={closeConfirmModal}>
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <CardContainer>
        <Table
          columns={tableColumns}
          visibleColumns={visibleColumns}
          loading={loading}
          search={true}
          viewOptions={
            <ListViewOptionsPanel
              columns={tableColumns}
              visibleColumns={visibleColumns}
              onToggleColumn={toggleColumnVisibility}
              onResetColumns={resetToDefault}
              showApply={false}
              showReset={false}
            />
          }
        >
          {variants.map((v) => (
            <Tr key={v._id} data={v}>
              {visibleColumns.includes(1) && (
                <Td>
                  <span
                    style={{
                      fontFamily: "monospace",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                    }}
                  >
                    {variantDisplay(v)}
                  </span>
                </Td>
              )}
              {visibleColumns.includes(2) && (
                <Td>
                  <span className="text-muted small">
                    {v.notes || v.label || "—"}
                  </span>
                </Td>
              )}
              {visibleColumns.includes(3) && (
                <Td>
                  {v.isActive ? (
                    <Lozenge appearance="success-subtle" isBold>
                      Aktif
                    </Lozenge>
                  ) : (
                    <Lozenge appearance="default-subtle" isBold>
                      Pasif
                    </Lozenge>
                  )}
                </Td>
              )}
              {visibleColumns.includes(4) && (
                <Td>
                  <div className="d-flex gap-2">
                    {canWrite && (
                      <>
                        <IconButton
                          icon={<FiEdit2 />}
                          label="Düzenle"
                          appearance="subtle"
                          shape="circle"
                          onClick={() => openEdit(v)}
                        />
                        <IconButton
                          icon={<FiTrash2 />}
                          label="Sil"
                          appearance="subtle"
                          shape="circle"
                          onClick={() => openDeleteConfirm(v)}
                        />
                      </>
                    )}
                  </div>
                </Td>
              )}
            </Tr>
          ))}
        </Table>
      </CardContainer>

      <SidebarContainer
        show={sidebarOpened}
        title={editingItem ? "Varyant Düzenle" : "Yeni Varyant"}
        onClose={closeSidebar}
      >
        <div className="d-flex flex-column gap-3" style={{ paddingBottom: 16 }}>
          <div>
            <Label required>Varyant</Label>
            <Input
              value={formData.variant}
              onChange={(v) => setFormData((s) => ({ ...s, variant: v }))}
              placeholder="örn. EU- veya A-"
            />
            <p className="text-muted small mt-1 mb-0">
              Parça adı ve kodunun başına eklenir.
            </p>
          </div>
          <div>
            <Label>Notlar</Label>
            <Input
              value={formData.notes}
              onChange={(v) => setFormData((s) => ({ ...s, notes: v }))}
              placeholder="örn. Avrupa pazarı"
            />
          </div>
          <div>
            <Select
              className="mb-0"
              label="Durum"
              unique
              options={[
                { value: "1", label: "Aktif" },
                { value: "0", label: "Pasif" },
              ]}
              selected={[formData.isActive ? "1" : "0"]}
              onSelected={(values) => {
                const active = values?.[0]?.value !== "0";
                setFormData((s) => ({ ...s, isActive: active }));
              }}
              placeholder="Durum seçin"
            />
          </div>
          <div className="pt-1">
            <Button
              appearance="primary-animated"
              onClick={handleSave}
              disabled={saving}
              icon={editingItem ? <FiEdit2 /> : <FiPlus />}
              className="w-100"
            >
              {saving
                ? "Kaydediliyor..."
                : editingItem
                  ? "Güncelle"
                  : "Oluştur"}
            </Button>
          </div>
        </div>
      </SidebarContainer>
    </AppPage>
  );
};

export default withAuth(PartVariants);
